import {useQuery} from '@tanstack/react-query';
import {globalApi} from 'api';
import Cookies from 'js-cookie';
import {Cart} from 'lib/Cart';
import {modalService} from 'lib/Modal';
import {formatNumber} from 'lib/formatters';
import {productsGridRootElement} from 'modules/ProductsGrid';
import {
    productsGridInitialState,
    productsGridReducer,
} from 'modules/ProductsGrid/containers/ProductsGridContainer/reducer';
import {
    calcProductsGridTotals,
    prepareDataForPdfGenerator,
} from 'modules/ProductsGrid/containers/ProductsGridContainer/utils';
import {ProductsGridTotals} from 'modules/ProductsGrid/lib/types';
import {useCallback, useEffect, useMemo, useReducer} from 'react';

// type ComplectsAmountOption = {
//     id: number;
//     label: string;
//     amount: number;
// };

// const OPTIONS: ComplectsAmountOption[] = [
//     {id: 1, label: '1 сет (4 персоны)', amount: 1},
//     {id: 2, label: '2 сета (8 персон)', amount: 2},
// ];

export function useProductsGridContainer() {
    const [{productIds, groupsIds, groups, products}, dispatch] = useReducer(
        productsGridReducer,
        productsGridInitialState,
    );

    // const [complectsAmountOptions, setComplectsAmountOptions] = useState<
    //     ComplectsAmountOption[]
    // >([]);
    // const [selectedComplectsAmount, setComplectsAmount] = useState<number>(1);

    // useEffect(() => {
    //     setComplectsAmountOptions(OPTIONS);
    // }, []);

    const {data: productsData} = useQuery({
        queryKey: ['solutionProducts'],
        queryFn: () => {
            const apiUrl = productsGridRootElement?.getAttribute('data-res');
            return globalApi.getSolutionProducts({apiUrl: apiUrl || 'test'});
        },
        staleTime: Infinity,
        retry: false,
    });

    useEffect(() => {
        if (productsData) {
            dispatch({type: 'setInitialState', payload: productsData});
        }
    }, [productsData]);

    const setProductDeleted = useCallback(
        ({
            id,
            deleted,
            groupId,
        }: {
            id: number;
            deleted: boolean;
            groupId: number;
        }) => {
            dispatch({
                type: 'setDeleted',
                payload: {productId: id, deleted, groupId},
            });
        },
        [],
    );

    const makeHandleRemove = useCallback(
        ({productId, groupId}: {productId: number; groupId: number}) =>
            () => {
                setProductDeleted({id: productId, deleted: true, groupId});
            },
        [setProductDeleted],
    );

    const makeHandleRestore = useCallback(
        ({productId, groupId}: {productId: number; groupId: number}) =>
            () => {
                setProductDeleted({id: productId, deleted: false, groupId});
            },
        [setProductDeleted],
    );

    const makeHandleChangeCount = useCallback(
        (productId: number) => (count: number) => {
            dispatch({type: 'setProductCount', payload: {productId, count}});
        },
        [],
    );

    const makeHandlePreviewClick = useCallback(
        (productId: number) => () => {
            const targetProduct = products[productId];

            const imgSrc =
                targetProduct.image_large || targetProduct.preview || '';
            const isSvg = imgSrc.includes('.svg');
            if (!isSvg && targetProduct.images) {
                const settings = {
                    type: 'gallery',
                    gallery: {
                        // @ts-ignore
                        images: targetProduct.images.map((urlImg) => ({
                            img: urlImg,
                            thumb: urlImg,
                        })),
                    },
                };
                modalService.showModal(
                    imgSrc,
                    settings,
                    // this.$el.querySelector(".solutions-grid__item-preview img")
                );
            }
        },
        [products],
    );

    const previewIsClickable = useMemo(() => {
        return (
            productsGridRootElement?.getAttribute('detail-view-enabled') !==
            null
        );
    }, []);

    // const handleComplectsAmountSelect = useCallback(
    //     (value: ComplectsAmountOption) => {
    //         setComplectsAmount(value.id);
    //         dispatch({
    //             type: 'setComplectsAmount',
    //             payload: {count: value.amount},
    //         });
    //     },
    //     [],
    // );

    const handleOrder = useCallback(() => {
        const solutionId =
            productsGridRootElement?.getAttribute('data-solution-id');

        if (solutionId) {
            Cookies.set('solution_id', solutionId, {
                expires: 5,
                path: '/',
            });
        }

        const disableMetrika = Boolean(
            productsGridRootElement?.getAttribute('data-disable-metrika'),
        );

        let added = 0;
        const allProducts = Object.values(products);
        allProducts.forEach((product) => {
            if (!product.deleted) {
                added++;
                Cart.update({
                    id: product.id,
                    price: product.price,
                    count: product.count,
                    discount: product.discount,
                    options: {
                        disableMetrika,
                    },
                });
            }
        });

        if (added) {
            const cartLink: string | null =
                //@ts-ignore
                document.getElementById('cart')?.href;
            if (cartLink) {
                window.location.href = cartLink;
            }
        }
    }, [products]);

    // Инициализируем глобальную функцию сабмита заказа.
    // Она будет вызываться за пределами React.
    useEffect(() => {
        window.ReactModules = window.ReactModules || {};
        window.ReactModules.submitSolutionOrder = handleOrder;
    }, [handleOrder]);

    const totals: ProductsGridTotals = useMemo(() => {
        return calcProductsGridTotals({
            groupsEntities: groups,
            productsEntities: products,
        });
    }, [groups, products]);

    // При изменении сумы заказа обновляем цену в соответствующем элементе на странице,
    // который находится за пределами React(рендерится на стороне PHP).
    useEffect(() => {
        const updatePrice = () => {
            const priceContainer = document.querySelector(
                '.solutions-item__review-price-value .price__current-value',
            );

            if (priceContainer) {
                priceContainer.innerHTML = formatNumber(
                    Math.round(totals.summary.price.current),
                );
            }
        };

        updatePrice();
    }, [totals]);

    // при изменении списка товаров, пересчитываем данные для генератора PDF.
    // Подумать над тем, чтобы делать это в момент клика, а не при каждом перерендере.
    // Для этого нужно вынести кнопку PDF в реакт
    useEffect(() => {
        const updatePdfGeneratorData = () => {
            const pdfButton = document.querySelector(
                '.js-modal.pdf[data-modal-settings]',
            );
            const currentSettingsJson = pdfButton?.getAttribute(
                'data-modal-settings',
            );

            if (pdfButton && currentSettingsJson) {
                const settings = JSON.parse(currentSettingsJson || '');
                const data = prepareDataForPdfGenerator({
                    groupsEntities: groups,
                    productsEntities: products,
                    groupsIds: groupsIds,
                    productIds: productIds,
                });

                settings.setCookie = {Solution: data.join('$')};
                (pdfButton as HTMLElement).dataset.modalSettings =
                    JSON.stringify(settings);
            }
        };

        updatePdfGeneratorData();
    }, [groups, products, groupsIds, productIds]);

    return {
        productIds,
        groupsIds,
        groups,
        products,
        makeHandleRemove,
        makeHandleRestore,
        makeHandleChangeCount,
        // handleComplectsAmountSelect,
        makeHandlePreviewClick,
        totals,
        // complectsAmountOptions,
        // selectedComplectsAmount,
        previewIsClickable,
    };
}
